import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that provides an AJAX-based product search for bulk order forms.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLInputElement} hiddenInput - Hidden input that stores the selected product value.
 * @property {HTMLElement} listbox - The listbox container for search results.
 * @property {HTMLElement} status - Status region for screen reader announcements.
 * @extends {Component<Refs>}
 */
class BulkOrderProductSearch extends Component {
  requiredRefs = ['searchInput', 'hiddenInput', 'listbox', 'status'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {number} */
  #currentIndex = -1;

  /** @type {Array<{title: string, url: string, image?: string, price: string, id: number}>} */
  #results = [];

  connectedCallback() {
    super.connectedCallback();
    this.#closeListbox();

    // Use event delegation for dynamically created options
    this.refs.listbox.addEventListener('mousedown', this.#onListboxMouseDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.listbox.removeEventListener('mousedown', this.#onListboxMouseDown);
  }

  /**
   * Handles mousedown on listbox items (mousedown fires before blur).
   * @param {MouseEvent} event - The mouse event.
   */
  #onListboxMouseDown = (event) => {
    const option = event.target.closest('[role="option"]');
    if (option) {
      event.preventDefault(); // Prevent blur from closing listbox
      const index = parseInt(option.dataset.index, 10);
      if (!isNaN(index)) {
        this.#selectResult(index);
      }
    }
  };

  /**
   * Handles input events with debouncing for search.
   */
  onInput = debounce(() => {
    const query = this.refs.searchInput.value.trim();

    if (query.length < 2) {
      this.#closeListbox();
      this.#updateStatus('');
      return;
    }

    this.#fetchProducts(query);
  }, 300);

  /**
   * Handles keyboard navigation within the search results.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onKeyDown = (event) => {
    const { listbox } = this.refs;
    const isOpen = !listbox.hidden;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen && this.refs.searchInput.value.trim().length >= 2) {
          this.#fetchProducts(this.refs.searchInput.value.trim());
        } else if (isOpen) {
          this.#navigateResults(1);
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          this.#navigateResults(-1);
        }
        break;

      case 'Enter':
        event.preventDefault();
        if (isOpen && this.#currentIndex >= 0) {
          this.#selectResult(this.#currentIndex);
        }
        break;

      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          this.#closeListbox();
          this.refs.searchInput.focus();
        }
        break;

      case 'Tab':
        if (isOpen) {
          this.#closeListbox();
        }
        break;
    }
  };

  /**
   * Handles focus leaving the component.
   * @param {FocusEvent} event - The focus event.
   */
  onBlur = (event) => {
    // Delay closing to allow click events on listbox items
    requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) {
        this.#closeListbox();
      }
    });
  };

  /**
   * Handles click on a result item.
   * @param {{ index: number }} data - Data containing the result index.
   */
  selectResult = (data) => {
    this.#selectResult(data.index);
  };

  /**
   * Clears the current selection.
   */
  clearSelection = () => {
    this.refs.searchInput.value = '';
    this.refs.hiddenInput.value = '';
    this.#closeListbox();
    this.refs.searchInput.focus();
  };

  /**
   * Fetches products from the search API.
   * @param {string} query - The search query.
   */
  async #fetchProducts(query) {
    // Cancel any pending fetch
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    this.#activeFetch = new AbortController();

    try {
      const url = new URL('/search/suggest.json', window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');

      const response = await fetch(url.toString(), {
        signal: this.#activeFetch.signal,
      });

      if (!response.ok) {
        throw new Error('Search request failed');
      }

      const data = await response.json();
      const products = data.resources?.results?.products || [];

      this.#results = products.map((product) => ({
        title: product.title,
        url: product.url,
        image: product.image,
        price: product.price,
        id: product.id,
      }));

      this.#renderResults();
      this.#updateStatus(
        this.#results.length === 0
          ? this.dataset.noResultsText || 'No products found'
          : `${this.#results.length} ${this.#results.length === 1 ? 'product' : 'products'} available`
      );
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.#updateStatus(this.dataset.errorText || 'Search error occurred');
      }
    }
  }

  /**
   * Renders the search results in the listbox.
   */
  #renderResults() {
    const { listbox } = this.refs;

    if (this.#results.length === 0) {
      listbox.innerHTML = `
        <div class="bulk-order-search__no-results">
          ${this.dataset.noResultsText || 'No products found'}
        </div>
      `;
      listbox.hidden = false;
      this.#currentIndex = -1;
      return;
    }

    listbox.innerHTML = this.#results
      .map(
        (product, index) => `
        <div
          role="option"
          id="product-option-${index}"
          class="bulk-order-search__option"
          aria-selected="false"
          on:click="/selectResult?index=${index}"
          data-index="${index}"
        >
          ${
            product.image
              ? `<img
                  src="${product.image}"
                  alt=""
                  class="bulk-order-search__option-image"
                  width="40"
                  height="40"
                  loading="lazy"
                >`
              : '<div class="bulk-order-search__option-image bulk-order-search__option-image--placeholder"></div>'
          }
          <div class="bulk-order-search__option-info">
            <span class="bulk-order-search__option-title">${this.#escapeHtml(product.title)}</span>
            <span class="bulk-order-search__option-price">${product.price}</span>
          </div>
        </div>
      `
      )
      .join('');

    listbox.hidden = false;
    this.#currentIndex = -1;
    this.refs.searchInput.setAttribute('aria-expanded', 'true');
  }

  /**
   * Navigates through results with keyboard.
   * @param {number} direction - Direction to navigate (1 for down, -1 for up).
   */
  #navigateResults(direction) {
    if (this.#results.length === 0) return;

    const newIndex = this.#currentIndex + direction;

    if (newIndex < 0) {
      this.#currentIndex = this.#results.length - 1;
    } else if (newIndex >= this.#results.length) {
      this.#currentIndex = 0;
    } else {
      this.#currentIndex = newIndex;
    }

    this.#highlightResult(this.#currentIndex);
  }

  /**
   * Highlights a result at the given index.
   * @param {number} index - Index of the result to highlight.
   */
  #highlightResult(index) {
    const options = this.refs.listbox.querySelectorAll('[role="option"]');

    options.forEach((option, i) => {
      const isSelected = i === index;
      option.setAttribute('aria-selected', String(isSelected));
      option.classList.toggle('bulk-order-search__option--highlighted', isSelected);
    });

    if (index >= 0) {
      this.refs.searchInput.setAttribute('aria-activedescendant', `product-option-${index}`);
      options[index]?.scrollIntoView({ block: 'nearest' });
    } else {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }
  }

  /**
   * Selects a result and updates the form fields.
   * @param {number} index - Index of the result to select.
   */
  #selectResult(index) {
    const product = this.#results[index];
    if (!product) return;

    this.refs.searchInput.value = product.title;
    this.refs.hiddenInput.value = product.title;
    this.#closeListbox();
    this.refs.searchInput.focus();
  }

  /**
   * Closes the listbox and resets state.
   */
  #closeListbox() {
    const { listbox, searchInput } = this.refs;
    listbox.hidden = true;
    searchInput.setAttribute('aria-expanded', 'false');
    searchInput.removeAttribute('aria-activedescendant');
    this.#currentIndex = -1;
  }

  /**
   * Updates the status region for screen readers.
   * @param {string} message - The message to announce.
   */
  #updateStatus(message) {
    this.refs.status.textContent = message;
  }

  /**
   * Escapes HTML characters to prevent XSS.
   * @param {string} text - Text to escape.
   * @returns {string} Escaped text.
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (!customElements.get('bulk-order-product-search')) {
  customElements.define('bulk-order-product-search', BulkOrderProductSearch);
}
